<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ChevronDown, MapPin, Menu, Search, X } from 'lucide-vue-next';
import { useCityStore } from '@/presentation/modules/city';
import { useQuery } from '@/presentation/composables';
import useSharedStore from '@/presentation/store/shared';
import BaseDialog from '@/presentation/components/base/dialog/BaseDialog.vue';
import BaseInput from '@/presentation/components/base/input/BaseInput.vue';

interface IProps {
	modelValueQuery?: string;
}

const props = withDefaults(defineProps<IProps>(), {
	modelValueQuery: '',
});

const emit = defineEmits<{
	(e: 'update:modelValueQuery', val: string): void;
	(e: 'search', query: string): void;
	(e: 'cityChanged', city: any): void;
}>();

const router = useRouter();
const cityStore = useCityStore();
const sharedStore = useSharedStore();
const activeRole = computed(() => sharedStore.userInformation?.role || 'AllUser');
const isAuthenticated = computed(() => Boolean(sharedStore.userInformation?.loggedIn && activeRole.value !== 'AllUser'));
const accountButtonLabel = computed(() => {
	if (!isAuthenticated.value) return 'ورود / ثبت‌نام';
	if (activeRole.value === 'Business') return 'پنل کسب‌وکار';
	if (activeRole.value === 'Professional') return 'پنل حرفه‌ای';
	if (activeRole.value === 'Partner') return 'پنل همکاری';
	if (activeRole.value === 'Admin') return 'پنل مدیریت';
	return 'حساب کاربری';
});

const searchQuery = ref(props.modelValueQuery);
const showCitiesDialog = ref(false);
const isMobileMenuOpen = ref(false);
const searchCityFilter = ref('');
const filteredCities = ref<any[]>([]);

const defaultCity = { id: 0, faName: 'مشهد', key: 'mashhad' };

const { data: rawCities } = useQuery({
	queryFn: cityStore.getCities,
	onSuccess: (response: any[]) => {
		const list = Array.isArray(response) ? response : [];
		filteredCities.value = list;
	},
});

const currentCityName = computed(() => {
	return cityStore.city?.faName || sharedStore.userInformation?.city || 'مشهد';
});

const logoSrc = computed(() => `${import.meta.env.BASE_URL || '/'}images/newty-logo.png`);

const route = useRoute();

const navLinks = computed(() => [
	{ title: 'خانه', path: '/home', active: route.path === '/home' },
	{ title: 'اکسپلور', path: '/explore', active: route.path.startsWith('/explore') },
	{ title: 'دسته‌بندی‌ها', path: '/category', active: route.path.startsWith('/category') },
	...(activeRole.value === 'User' || activeRole.value === 'AllUser'
		? [{ title: 'رزروهای من', path: '/requests', active: route.path.startsWith('/requests') }]
		: []),
	{ title: 'تماس با ما', path: '/contact-us', active: route.path === '/contact-us' },
]);

const handleSearch = () => {
	emit('search', searchQuery.value);
	router.push({
		path: '/explore',
		query: {
			q: searchQuery.value || undefined,
			city: currentCityName.value,
		},
	});
};

const filterCities = (query: string) => {
	const all = Array.isArray(rawCities.value) ? rawCities.value : [];
	if (!query) {
		filteredCities.value = all;
		return;
	}
	filteredCities.value = all.filter(
		(c: any) =>
			c.faName?.includes(query) ||
			c.key?.toLowerCase().includes(query.toLowerCase()) ||
			c.alternatives?.some((alt: string) => alt.toLowerCase().includes(query.toLowerCase())),
	);
};

const selectCity = (city: any) => {
	cityStore.setCity(city);
	showCitiesDialog.value = false;
	emit('cityChanged', city);
};

const handleAccountButton = () => {
	if (!isAuthenticated.value) {
		router.push({ name: 'Login' });
		return;
	}

	const accountPaths: Record<string, string> = {
		Business: '/dashboard',
		Professional: '/dashboard/professional',
		Partner: '/dashboard/partner',
		Admin: '/dashboard/admin',
		User: '/setting',
	};
	router.push(accountPaths[activeRole.value] || '/home');
};
</script>

<template>
	<header dir="rtl" class="sticky top-0 z-40 w-full border-b border-slate-100 bg-white/95 backdrop-blur-md transition">
		<div class="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
			<!-- Right Side: Logo & Navigation Links -->
			<div class="flex items-center gap-6 xl:gap-8">
				<RouterLink to="/home" class="flex items-center gap-2" aria-label="Newty">
					<img :src="logoSrc" alt="Newty Logo" class="h-9 w-auto object-contain sm:h-10" />
				</RouterLink>

				<!-- Navigation Menu (Desktop) -->
				<nav class="hidden items-center gap-5 text-[15px] font-medium text-slate-700 lg:flex xl:gap-7">
					<template v-for="link in navLinks" :key="link.title">
						<RouterLink
							v-if="!link.path.startsWith('http')"
							:to="link.path"
							class="relative py-2 font-bold transition hover:text-[#4848FE]"
							:class="link.active ? 'text-[#4848FE]' : 'text-slate-700'"
						>
							{{ link.title }}
							<span v-if="link.active" class="absolute bottom-0 right-0 h-[3px] w-full rounded-full bg-[#4848FE]"></span>
						</RouterLink>
						<a v-else :href="link.path" target="_blank" class="py-2 transition hover:text-[#4848FE]">
							{{ link.title }}
						</a>
					</template>
				</nav>
			</div>

			<!-- Center/Left Side: Search Bar, City Selector, Login Button, Mobile Menu -->
			<div class="flex items-center gap-3">
				<!-- Search Input (Desktop) using BaseInput -->
				<div class="relative hidden w-[260px] xl:w-[320px] md:block">
					<BaseInput
						v-model="searchQuery"
						name="navSearch"
						placeholder="جستجو برای خدمات یا سالن..."
						next-icon="search"
						:floating-label="false"
						input-class="!mb-0"
						main-class="!h-11 !min-h-[44px] !rounded-2xl !border-slate-200 !bg-slate-50/70 focus-within:!border-[#4848FE] focus-within:!bg-white"
						@click-next-image="handleSearch"
						@keyup.enter="handleSearch"
					/>
				</div>

				<!-- City Selector Pill -->
				<button
					type="button"
					class="flex h-11 items-center gap-1.5 rounded-2xl border border-slate-200 bg-slate-50/70 px-3.5 text-xs font-bold text-slate-700 transition hover:border-[#4848FE]/40 hover:bg-white"
					@click="showCitiesDialog = true"
				>
					<MapPin :size="16" class="text-slate-500" />
					<span>{{ currentCityName }}</span>
					<ChevronDown :size="15" class="text-slate-400" />
				</button>

				<!-- Login / Register Button -->
				<button
					type="button"
					class="hidden h-11 items-center rounded-2xl bg-[#4848FE] px-5 text-sm font-bold text-white shadow-sm transition hover:bg-[#3737e8] sm:flex"
					@click="handleAccountButton"
				>
					{{ accountButtonLabel }}
				</button>

				<!-- Mobile Menu Button -->
				<button
					type="button"
					class="flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-700 transition hover:border-[#4848FE] lg:hidden"
					aria-label="منوی سایت"
					@click="isMobileMenuOpen = !isMobileMenuOpen"
				>
					<Menu :size="22" />
				</button>
			</div>
		</div>

		<!-- Mobile Slide-over Drawer (Teleported to body for reliable stacking and solid background) -->
		<Teleport to="body">
			<div
				v-if="isMobileMenuOpen"
				class="fixed inset-0 z-[100] flex justify-end bg-slate-950/60 backdrop-blur-sm lg:hidden transition-opacity"
				@click.self="isMobileMenuOpen = false"
			>
				<div dir="rtl" class="relative flex h-full w-[300px] max-w-[85vw] flex-col bg-white p-6 shadow-2xl z-[101]">
					<!-- Drawer Header -->
					<div class="flex items-center justify-between border-b border-slate-100 pb-4">
						<img :src="logoSrc" alt="Newty" class="h-8 w-auto object-contain" />
						<button
							type="button"
							class="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
							aria-label="بستن"
							@click="isMobileMenuOpen = false"
						>
							<X :size="18" />
						</button>
					</div>

					<!-- Drawer Search using BaseInput -->
					<div class="mt-4">
						<BaseInput
							v-model="searchQuery"
							name="mobileSearch"
							placeholder="جستجو برای خدمات یا سالن..."
							next-icon="search"
							:floating-label="false"
							input-class="!mb-0"
							main-class="!h-11 !min-h-[44px] !rounded-xl !border-slate-200 !bg-slate-50"
							@click-next-image="
								() => {
									handleSearch();
									isMobileMenuOpen = false;
								}
							"
							@keyup.enter="
								() => {
									handleSearch();
									isMobileMenuOpen = false;
								}
							"
						/>
					</div>

					<!-- City Selection Button in Drawer -->
					<button
						type="button"
						class="mt-3 flex h-11 items-center justify-between rounded-xl border border-slate-200 bg-slate-50/80 px-4 text-xs font-bold text-slate-700 transition hover:border-[#4848FE]/40"
						@click="
							() => {
								showCitiesDialog = true;
								isMobileMenuOpen = false;
							}
						"
					>
						<div class="flex items-center gap-2">
							<MapPin :size="16" class="text-[#4848FE]" />
							<span>شهر: {{ currentCityName }}</span>
						</div>
						<ChevronDown :size="14" class="text-slate-400" />
					</button>

					<!-- Navigation Links -->
					<nav class="mt-5 flex flex-col gap-1.5 text-sm font-bold text-slate-700">
						<template v-for="link in navLinks" :key="link.title">
							<RouterLink
								v-if="!link.path.startsWith('http')"
								:to="link.path"
								class="flex items-center justify-between rounded-xl px-3.5 py-2.5 transition hover:bg-slate-50 hover:text-[#4848FE]"
								:class="link.active ? 'bg-[#4848FE]/5 text-[#4848FE]' : 'text-slate-700'"
								@click="isMobileMenuOpen = false"
							>
								<span>{{ link.title }}</span>
								<span v-if="link.active" class="h-2 w-2 rounded-full bg-[#4848FE]"></span>
							</RouterLink>
							<a
								v-else
								:href="link.path"
								target="_blank"
								class="flex items-center justify-between rounded-xl px-3.5 py-2.5 text-slate-700 transition hover:bg-slate-50 hover:text-[#4848FE]"
								@click="isMobileMenuOpen = false"
							>
								<span>{{ link.title }}</span>
							</a>
						</template>
					</nav>

					<!-- Bottom Auth Button -->
					<div class="mt-auto border-t border-slate-100 pt-5">
						<button
							type="button"
							class="flex h-12 w-full items-center justify-center rounded-xl bg-[#4848FE] text-sm font-bold text-white shadow-md transition hover:bg-[#3737e8]"
							@click="
								() => {
									handleAccountButton();
									isMobileMenuOpen = false;
								}
							"
						>
							{{ accountButtonLabel }}
						</button>
					</div>
				</div>
			</div>
		</Teleport>

		<!-- City Selection Dialog -->
		<BaseDialog v-model="showCitiesDialog" title="انتخاب شهر" show-close full-screen @toggle="showCitiesDialog = !showCitiesDialog">
			<template #body>
				<div class="p-2">
					<BaseInput v-model="searchCityFilter" name="cityFilter" next-icon="search" placeholder="جستجوی شهر..." @update:model-value="filterCities" />
					<div class="mt-4 max-h-[380px] overflow-y-auto pr-1">
						<div
							v-for="item in filteredCities.length ? filteredCities : [defaultCity]"
							:key="item.id || item.key"
							class="mb-2 flex cursor-pointer items-center justify-between rounded-2xl border border-slate-100 p-3.5 transition hover:border-[#4848FE]/30 hover:bg-slate-50"
							:class="{ '!border-[#4848FE] !bg-[#4848FE]/5 text-[#4848FE]': item.faName === currentCityName }"
							@click="selectCity(item)"
						>
							<div class="flex items-center gap-2">
								<MapPin :size="18" :class="item.faName === currentCityName ? 'text-[#4848FE]' : 'text-slate-400'" />
								<span class="text-sm font-bold">{{ item.faName }}</span>
							</div>
							<span v-if="item.faName === currentCityName" class="text-xs font-black text-[#4848FE]">انتخاب شده</span>
						</div>
					</div>
				</div>
			</template>
		</BaseDialog>
	</header>
</template>
